import {
	DragEvent,
	useCallback,
	useMemo,
	useState
} from "react";
import { DropzoneContextData } from "client/Contexts/DropzoneContext";
import {
	DragOverEventArgs,
	DragOverEventCallbacks,
	DragOverEventResult,
	DropEventArgs,
	useDragAndDropEvents
} from "client/Hooks";
import { intersection } from "client_legacy/ndaw/arrayHelpers";

export type DropzoneHandlers = {
	onDragOver?(event: DragEvent): void;
	onDragEnter?(event: DragEvent): void;
	onDragLeave?(event: DragEvent): void;
	onDrop?(event: DragEvent): void;
	dropzoneContext: DropzoneContextData;
	isDropzoneActive: boolean;
};

export function useDropzoneHandlers(
	isEnabled: boolean
): DropzoneHandlers {
	const [isDropTarget, setIsDropTarget] = useState<boolean>(false);
	const [dropzoneListeners, setDropzoneListeners] = useState<readonly DropzoneListener[]>([]);

	const dragOverHandler = useCallback(
		(args: DragOverEventArgs): DragOverEventResult => {
			for (const listener of dropzoneListeners) {
				if (canHandle(listener, args.types)) {
					return {
						isAccepted: true
					};
				}
			}

			return {
				isAccepted: false
			};
		},
		[dropzoneListeners]
	);

	const dragEnterHandler = useCallback(
		(args: DragOverEventArgs): void => {
			for (const listener of dropzoneListeners) {
				if (canHandle(listener, args.types)) {
					setIsDropTarget(true);
					return;
				}
			}

			setIsDropTarget(false);
		},
		[dropzoneListeners]
	);

	const dragLeaveHandler = useCallback(
		(): void => {
			setIsDropTarget(false);
		},
		[]
	);

	const dropHandler = useCallback(
		async (args: DropEventArgs): Promise<void> => {
			for (const listener of dropzoneListeners) {
				const wasHandled: boolean = await listener.callback(args);

				if (wasHandled) {
					break;
				}
			}
		},
		[dropzoneListeners]
	);

	const dragCallbacks = useMemo(
		(): DragOverEventCallbacks => {
			if (!isEnabled) {
				return {};
			}

			return {
				onDragEnter: dragEnterHandler,
				onDragOver: dragOverHandler,
				onDragLeave: dragLeaveHandler,
				onDrop: dropHandler
			};
		},
		[
			dragEnterHandler,
			dragLeaveHandler,
			dragOverHandler,
			dropHandler,
			isEnabled
		]
	);

	const {
		isDropTarget: _,
		...dragHandlers
	} = useDragAndDropEvents(dragCallbacks);

	const dropzoneContext = useMemo(
		(): DropzoneContextData => ({
			register: (
				acceptedTypes: readonly string[],
				callback: (args: DropEventArgs) => Promise<boolean>
			) => {
				const listener: DropzoneListener = {
					acceptedTypes,
					callback
				};

				setDropzoneListeners(
					(x) => [...x, listener]
				);

				return (): void => {
					setDropzoneListeners(
						(x) => x.filter(l => l!== listener)
					);
				};
			}
		}),
		[]
	);

	return {
		...dragHandlers,
		dropzoneContext,
		isDropzoneActive: isDropTarget
	};
}

type DropzoneListener = {
	acceptedTypes: readonly string[];
	callback: (args: DropEventArgs) => Promise<boolean>;
};

function canHandle(listener: DropzoneListener, types: readonly string[]): boolean {
	return intersection(listener.acceptedTypes, types)
		.length > 0;
}
